import { supabase } from '../lib/supabase';

/**
 * Service for managing service requests
 */
export const serviceRequestService = {
  /**
   * Fetch available service requests for authenticated professional
   * Filters by professional's work areas and payment authorization status
   * @returns {Promise<{data: Array, error?: string}>}
   */
  async fetchAvailableRequests() {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check payment authorization
      const { data: userData, error: userDataError } = await supabase
        ?.from('users')?.select('payment_authorized')?.eq('id', user?.id)
        ?.single();

      if (userDataError) {
        throw new Error(`Failed to check payment authorization: ${userDataError?.message}`);
      }

      if (!userData?.payment_authorized) {
        return {
          data: [],
          error: 'Payment authorization required to view service requests'
        };
      }

      // Fetch service requests
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch service requests: ${error?.message}`);
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Error fetching service requests:', error);
      return {
        data: [],
        error: error?.message || 'Si è verificato un errore durante il caricamento delle richieste'
      };
    }
  },

  /**
   * Subscribe to real-time service request changes
   * @param {Function} callback - Function to call when changes occur
   * @returns {Object} Supabase channel subscription
   */
  subscribeToRequests(callback) {
    const channel = supabase
      ?.channel('service_requests_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_requests',
          filter: 'status=eq.pending'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time changes
   * @param {Object} channel - Supabase channel to unsubscribe
   */
  unsubscribeFromRequests(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Accept a service request
   * @param {string} requestId - Service request ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async acceptRequest(requestId) {
    try {
      if (!requestId || requestId === 'undefined') {
        throw new Error('ID richiesta non valido');
      }

      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        ?.from('service_requests')
        ?.update({
          assigned_professional_id: user?.id,
          status: 'accepted'
        })
        ?.eq('id', requestId)
        ?.eq('status', 'pending');

      if (error) {
        throw new Error(`Failed to accept request: ${error?.message}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error accepting request:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante l\'accettazione della richiesta'
      };
    }
  },

  /**
   * Upload media files to storage for a service request
   * @param {string} requestId - Service request ID
   * @param {Array<File>} files - Array of processed image files
   * @returns {Promise<{success: boolean, uploadedFiles?: Array, error?: string}>}
   */
  async uploadServiceRequestMedia(requestId, files) {
    try {
      if (!files || files?.length === 0) {
        return { success: true, uploadedFiles: [] };
      }

      const uploadedFiles = [];
      const errors = [];

      for (const file of files) {
        try {
          // Generate unique filename with timestamp
          const timestamp = Date.now();
          const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
          const fileName = `${timestamp}-${sanitizedName}`;
          const filePath = `${requestId}/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase
            ?.storage
            ?.from('service-requests')
            ?.upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error(`Failed to upload ${file?.name}:`, error);
            errors?.push(`${file?.name}: ${error?.message}`);
            continue;
          }

          // Determine if file is previewable
          const previewableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
          const isPreviewable = previewableTypes?.includes(file?.type?.toLowerCase());

          // Store file metadata
          uploadedFiles?.push({
            storage_path: data?.path,
            original_filename: file?.name,
            mime_type: file?.type,
            previewable: isPreviewable,
            uploaded_at: new Date()?.toISOString()
          });
        } catch (fileError) {
          console.error(`Error processing ${file?.name}:`, fileError);
          errors?.push(`${file?.name}: ${fileError?.message}`);
        }
      }

      // Update service_requests record with media metadata
      if (uploadedFiles?.length > 0) {
        const { error: updateError } = await supabase
          ?.from('service_requests')
          ?.update({ media_files: uploadedFiles })
          ?.eq('id', requestId);

        if (updateError) {
          console.error('Failed to update media metadata:', updateError);
          return {
            success: false,
            error: 'File upload succeeded but failed to save metadata'
          };
        }
      }

      return {
        success: true,
        uploadedFiles,
        ...(errors?.length > 0 && { partialErrors: errors })
      };
    } catch (error) {
      console.error('Error uploading service request media:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante il caricamento dei file'
      };
    }
  },

  /**
   * Get signed URLs for service request media files
   * @param {Array} mediaFiles - Array of media file metadata from service_requests.media_files
   * @returns {Promise<{success: boolean, urls?: Array, error?: string}>}
   */
  async getServiceRequestMediaUrls(mediaFiles) {
    try {
      if (!mediaFiles || mediaFiles?.length === 0) {
        return { success: true, urls: [] };
      }

      const urlPromises = mediaFiles?.map(async (file) => {
        try {
          // Generate signed URL (valid for 1 hour)
          const { data, error } = await supabase
            ?.storage
            ?.from('service-requests')
            ?.createSignedUrl(file?.storage_path, 3600);

          if (error) {
            console.error(`Failed to generate URL for ${file?.original_filename}:`, error);
            return null;
          }

          return {
            ...file,
            signed_url: data?.signedUrl
          };
        } catch (err) {
          console.error(`Error processing ${file?.original_filename}:`, err);
          return null;
        }
      });

      const urls = (await Promise.all(urlPromises))?.filter(url => url !== null);

      return { success: true, urls };
    } catch (error) {
      console.error('Error getting service request media URLs:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante il caricamento delle immagini'
      };
    }
  },

  /**
   * Create a new service request from client
   * @param {Object} requestData - Service request data
   * @param {string} requestData.serviceType - Type of service requested
   * @param {string} requestData.title - Request title
   * @param {string} requestData.description - Detailed description
   * @param {string} requestData.locationMapboxId - Mapbox location ID
   * @param {string} requestData.locationName - Location name
   * @param {string} requestData.urgency - Urgency level (low, medium, high, urgent)
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async createServiceRequest(requestData) {
    try {
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // GUARD: Daily limit check — max 3 requests per service_type per day
      const todayStart = new Date();
      todayStart?.setHours(0, 0, 0, 0);

      const { count: dailyCount, error: dailyError } = await supabase
        ?.from('service_requests')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('client_id', user?.id)
        ?.eq('service_type', requestData?.serviceType)
        ?.gte('created_at', todayStart?.toISOString());

      if (dailyError) {
        console.error('Error checking daily request count:', dailyError);
        throw new Error('Impossibile verificare il limite giornaliero');
      }

      if (dailyCount >= 3) {
        return {
          success: false,
          error: 'Hai raggiunto il limite di 3 richieste giornaliere per questo servizio.'
        };
      }

      // GUARD: Check active request count limit (max 5)
      const { count, error: countError } = await supabase
        ?.from('service_requests')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('client_id', user?.id)
        ?.eq('status', 'active');

      if (countError) {
        console.error('Error checking active request count:', countError);
        throw new Error('Impossibile verificare il numero di richieste attive');
      }

      // Enforce maximum of 5 active requests per client
      if (count >= 5) {
        return {
          success: false,
          error: 'Hai raggiunto il limite massimo di 5 richieste attive. Completa o annulla una richiesta esistente prima di crearne una nuova.'
        };
      }

      // Prepare insert data with only existing schema fields
      const insertData = {
        client_id: user?.id,
        service_type: requestData?.serviceType,
        title: requestData?.title,
        description: requestData?.description,
        location_mapbox_id: requestData?.locationMapboxId,
        location_name: requestData?.locationName,
        urgency: requestData?.urgency || 'medium',
        status: 'active',
        created_at: new Date()?.toISOString(),
        preferred_date: requestData?.preferredDate || null,
        preferred_time_slot: requestData?.preferredTimeSlot || null,
        additional_details: requestData?.additionalDetails || null,
        assigned_professional_id: requestData?.assignedProfessionalId || null,
        source: requestData?.source || 'marketplace',
        source_slug: requestData?.sourceSlug || null,
        installments_requested: requestData?.installmentsRequested === true,
      };

      // Insert into service_requests table
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.insert([insertData])
        ?.select()
        ?.single();

      if (error) {
        throw new Error(`Failed to create service request: ${error?.message}`);
      }

      // Clear sessionStorage pending professional intent after successful insert
      if (insertData?.assigned_professional_id) {
        try {
          sessionStorage.removeItem('volaptic_pending_request_pro');
        } catch (_) {}
      }

      return { 
        success: true, 
        data 
      };
    } catch (error) {
      console.error('Error creating service request:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante la creazione della richiesta'
      };
    }
  },

  /**
   * Fetch authenticated client's active service request
   * @returns {Promise<{data?: Object, error?: string}>}
   */
  async fetchClientActiveRequest() {
    try {
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Fetch active service request for this client
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('client_id', user?.id)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      if (error) {
        throw new Error(`Failed to fetch active request: ${error?.message}`);
      }

      return { data };
    } catch (error) {
      console.error('Error fetching client active request:', error);
      return {
        error: error?.message || 'Si è verificato un errore durante il caricamento della richiesta'
      };
    }
  },

  /**
   * Fetch a single service request by ID for authenticated client
   * Ensures the request belongs to the authenticated client
   * @param {string} requestId - Service request ID
   * @returns {Promise<{data?: Object, error?: string}>}
   */
  async fetchClientRequestById(requestId) {
    try {
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Fetch service request by ID with ownership verification
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('id', requestId)
        ?.eq('client_id', user?.id)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          throw new Error('Richiesta non trovata o non autorizzata');
        }
        throw new Error(`Failed to fetch request: ${error?.message}`);
      }

      return { data };
    } catch (error) {
      console.error('Error fetching client request by ID:', error);
      return {
        error: error?.message || 'Si è verificato un errore durante il caricamento della richiesta'
      };
    }
  }
};

/**
 * Check if an active service request already exists for the given service type and user
 * @param {string} serviceType - The service type to check
 * @param {string} userId - The client user ID
 * @returns {Promise<{hasDuplicate: boolean, existingId: string|null}>}
 */
export async function checkDuplicateServiceType(serviceType, userId) {
  try {
    const { data, error } = await supabase
      ?.from('service_requests')
      ?.select('id')
      ?.eq('client_id', userId)
      ?.eq('service_type', serviceType)
      ?.eq('status', 'active')
      ?.maybeSingle();

    if (error) {
      console.error('Error checking duplicate service type:', error);
      return { hasDuplicate: false, existingId: null };
    }

    return {
      hasDuplicate: !!data,
      existingId: data?.id ?? null
    };
  } catch (err) {
    console.error('Error in checkDuplicateServiceType:', err);
    return { hasDuplicate: false, existingId: null };
  }
}

export default serviceRequestService;
